/** @file Advances all tracks by dt seconds and appends lifecycle and keyed events to outEvents. */
import { advanceEntry } from '../_internal/advanceEntry';
import { coreFail } from '../_internal/coreFail';
import { releaseFixLayer } from '../_internal/direction/releaseFixLayer';
import { mixWeight } from '../_internal/mixWeight';
import type { AnimEvent, AnimState, TrackEntry } from '../contracts/state';
import type { SkeletonData } from '../skeletonData';
import { BASE_TRACKS } from './baseTracks.data';
import { loopPass } from './loopPass';

/** Finite and >= 0: a number of seconds, or a timeScale stepState can play (reverse playback is not Wave 2, R10-03). */
const playable = (v: number): boolean => Number.isFinite(v) && v >= 0;

/** Throws VALIDATION at `${path}.timeScale` for an entry whose timeScale stepState cannot play. */
function failTimeScale(path: string, timeScale: number): never {
  return coreFail('VALIDATION', `timeScale must be a finite number >= 0 (no reverse playback), got ${String(timeScale)}`, { path: `${path}.timeScale` });
}

/**
 * Checks every entry track t will advance (the current entry, its from-chain, its queue) before anything moves, so
 * a bad timeScale leaves the state untouched; paths are built only on failure (no allocation on the happy path).
 */
function checkTrack(state: AnimState, t: number): void {
  let depth = 0;
  for (let e = state.tracks[t] ?? null; e !== null; e = e.from, depth++) {
    if (!playable(e.timeScale)) failTimeScale(`tracks[${String(t)}]${'.from'.repeat(depth)}`, e.timeScale);
  }
  const queue = state.queue[t] ?? [];
  for (let i = 0; i < queue.length; i++) {
    const e = queue[i];
    if (e !== undefined && !playable(e.timeScale)) failTimeScale(`queue[${String(t)}][${String(i)}]`, e.timeScale);
  }
}

/** Emits end + dispose for entry and every entry below it in its from-chain (newest first). */
function disposeChain(track: number, entry: TrackEntry, out: AnimEvent[]): void {
  for (let e: TrackEntry | null = entry; e !== null; e = e.from) {
    out.push({ type: 'end', track, anim: e.anim });
    out.push({ type: 'dispose', track, anim: e.anim });
  }
}

/** Ends link's mix: drops its from-chain (end + dispose) and its mixDirs (null without a from-entry, CR-007). */
function endMix(track: number, link: TrackEntry, out: AnimEvent[]): void {
  const from = link.from;
  link.from = null;
  link.mixDirs = null;
  if (from !== null) disposeChain(track, from, out);
}

/**
 * A mirrorFix chain's lower links keep their owners' clocks: they advance with no events and no mix timer of their
 * own; only releaseFixLayer unlinks them, when their owners leave the base track (R19-02 follow-up).
 */
function advanceFixLinks(data: SkeletonData, track: number, entry: TrackEntry, dt: number, out: AnimEvent[]): void {
  for (let e = entry.from; e !== null; e = e.from) advanceEntry(data, track, e, dt, false, false, out);
}

/**
 * Advances every entry the current one mixes from. A from-entry fires keys and complete only while the weight of
 * the entry mixing it out is below its eventThreshold; a finished link drops its from-chain. The deepest link has no
 * from-entry: on a track above 0, while it fades in (mixTime < mixDuration, spec §5.7 m3) its mixTime grows by its
 * own clock's advance (after any delay), the weight applyState gives it over the lower tracks; on track 0 it never
 * fades (R19-01), so its mixTime stays as set.
 */
function stepMixingOut(data: SkeletonData, track: number, entry: TrackEntry, dt: number, out: AnimEvent[]): void {
  let link = entry;
  while (link.from !== null) {
    const from = link.from;
    advanceEntry(data, track, from, dt, false, mixWeight(link) < from.eventThreshold, out);
    link.mixTime += dt;
    if (link.mixTime >= link.mixDuration) {
      endMix(track, link, out);
      return;
    }
    link = from;
  }
  if (track > 0 && link.mixTime < link.mixDuration) link.mixTime += link.time - link.lastTime;
}

/**
 * Whether current's clock has reached a queued take-over time `delay`, with loopPass's slack measured in current's
 * passes: a take-over on a loop boundary (addAnimation's default with no mix) lands in the step whose complete
 * marks that boundary, even when the clock sits a float error below it. Without a pass length: a plain compare.
 */
function reached(data: SkeletonData, current: TrackEntry, delay: number): boolean {
  const pass = (data.animations[current.anim]?.duration ?? 0) / current.timeScale;
  return pass > 0 && Number.isFinite(pass) ? loopPass(current.time - delay, pass) >= 0 : current.time >= delay;
}

/**
 * Promotes queued entries whose delay the current clock has reached. The overshoot, clamped to this step's dt
 * (R3-01: a held entry already past the delay never jumps the new clock) and to >= 0, becomes their first advance.
 * Only base tracks have queues (addAnimation); the track's mirrorFix layer fades out with the entry taken over and
 * leaves with it (stepState releases it, R19-02).
 */
function promoteQueued(state: AnimState, data: SkeletonData, track: number, dt: number, out: AnimEvent[]): void {
  const queue = state.queue[track];
  let current = state.tracks[track] ?? null;
  while (queue !== undefined && current !== null && current.lastTime >= 0) {
    const next = queue[0];
    if (next === undefined || !reached(data, current, next.delay)) return;
    queue.shift();
    const over = Math.max(0, Math.min(current.time - next.delay, dt));
    next.delay = 0;
    next.from = current;
    next.mixDirs = new Float64Array(data.bones.length);
    state.tracks[track] = next;
    advanceEntry(data, track, next, over, true, true, out);
    next.mixTime = over;
    if (next.mixTime >= next.mixDuration) endMix(track, next, out);
    current = next;
  }
}

/**
 * Pure step: mutates state timing only; events are returned through outEvents (never callbacks). Per track, in
 * ascending order: the current entry, then its from-chain, then queued entries. A looping entry fires at most one
 * loop plus the remainder per step; skipped loops fire nothing (silent cap, W2-12). VALIDATION (state untouched)
 * when dt is not finite seconds >= 0 (path `dt`) or an entry's timeScale is not finite or is < 0 (R10-03; AnimState
 * has no timeScale of its own, so an entry's is the effective one). timeScale 0 holds the entry and fires nothing.
 */
export function stepState(state: AnimState, data: SkeletonData, dt: number, outEvents: AnimEvent[]): void {
  if (!playable(dt)) coreFail('VALIDATION', `dt must be finite seconds >= 0, got ${String(dt)}`, { path: 'dt' });
  for (let t = 0; t < state.tracks.length; t++) checkTrack(state, t);
  for (let t = 0; t < state.tracks.length; t++) {
    const entry = state.tracks[t];
    if (entry === null || entry === undefined) continue;
    advanceEntry(data, t, entry, dt, true, true, outEvents);
    if (t >= BASE_TRACKS) {
      advanceFixLinks(data, t, entry, dt, outEvents);
      continue;
    }
    if (entry.lastTime >= 0) stepMixingOut(data, t, entry, dt, outEvents);
    promoteQueued(state, data, t, dt, outEvents);
    // A base track's mirrorFix layer leaves once the entry it belongs to was disposed above (R19-02).
    if (t < BASE_TRACKS) releaseFixLayer(state, t);
  }
}
